/**
 * Блок «Разработанные инструкции по охране труда» шага «Анкета» визарда профрисков
 * (требование «Шаг 2. Анкета»): вопрос «да/нет» → способ передачи списка → загруженные файлы
 * либо ручной список наименований. Смена способа передачи и ответ «Нет» при уже введённых
 * данных подтверждаются диалогом.
 *
 * Выделено из `profrisk-wizard.tsx` без изменения поведения, текстов и разметки: оркестратор
 * `InstructionsBlock` держит состояние и сценарий, части (`MethodPicker`, `FilesMethod` с
 * `UploadZone`/`FileList`, `ManualList`, `ConfirmDialog`) отвечают каждая за свой кусок экрана.
 */
import { useRef, useState, type RefObject } from 'react'
import { Plus, Trash2, Upload } from 'lucide-react'
import {
  profriskApi,
  ApiError,
  type ProfriskInstruction,
  type ProfriskInstructionFile,
  type ProfriskInstructionsDeveloped,
  type ProfriskInstructionsMethod,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { cn } from '@/lib/utils'
import {
  PROFRISK_INSTRUCTIONS_ERRORS,
  emptyInstruction,
  instructionsHaveData,
  methodHasData,
  type ProfriskInstructionsErrorField,
} from '@/lib/profrisk'
import {
  PROFRISK_CONFIRM_CANCEL,
  PROFRISK_CONFIRM_CONTINUE,
  PROFRISK_CONFIRM_METHOD_TEXT,
  PROFRISK_CONFIRM_METHOD_TITLE,
  PROFRISK_CONFIRM_NONE_TEXT,
  PROFRISK_CONFIRM_NONE_TITLE,
  PROFRISK_INSTRUCTIONS_METHOD_QUESTION,
  PROFRISK_INSTRUCTIONS_QUESTION,
  PROFRISK_INSTRUCTIONS_TITLE,
  PROFRISK_MANUAL_ADD_BUTTON,
  PROFRISK_MANUAL_FIRST_PLACEHOLDER,
  PROFRISK_MANUAL_NAME_LABEL,
  PROFRISK_METHOD_CARDS,
  PROFRISK_NO,
  PROFRISK_UPLOAD_DELETE_BUTTON,
  PROFRISK_UPLOAD_PICK_BUTTON,
  PROFRISK_UPLOAD_REPLACE_BUTTON,
  PROFRISK_UPLOAD_ZONE_FORMATS,
  PROFRISK_UPLOAD_ZONE_TEXT,
  PROFRISK_YES,
} from './profrisk-copy'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { Alert } from '@/design-system/components/feedback/alert'
import { ServicePickCard } from '@/design-system/components/data/service-pick-card'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'

// --- Общий сегментированный выбор «Да»/«Нет» ---

/** Выделение выбранного ответа: акцентный контур на светлой заливке (см. YesNoGroup). */
const SELECTED_ANSWER = 'border-accent bg-accent-bg text-accent-txt hover:bg-accent-bg'

/**
 * Пара кнопок «Да»/«Нет» с единственным выбранным значением. Готового сегментированного
 * контрола в ДС нет; следуем уже принятому в проекте паттерну (поле «Пол» карточки сотрудника,
 * `company/employees-manager.tsx`): `role="group"` + две `Button` и `aria-pressed`.
 * Клавиатура и видимый фокус — из самой `Button` (`focus-visible:ring-*`).
 *
 * Выбранная кнопка выделяется акцентным контуром на светлой заливке
 * (`border-accent bg-accent-bg text-accent-txt`, тот же приём, что у чипов-фильтров
 * `pages/ot-overview.tsx`), а НЕ сплошной `variant="primary"` — так кнопка «Да»/«Нет» показана
 * на скриншоте-ориентире к требованию (centrika-docs!25, «Ориентир для дизайна и компоновки
 * шага „Анкета“»).
 *
 * Живёт здесь и переиспользуется визардом (блок «Что уже есть на предприятии») — так импорт
 * идёт в одну сторону, визард → блок инструкций, без циклической зависимости модулей.
 */
export function YesNoGroup({
  label,
  value,
  disabled,
  describedBy,
  onChange,
}: {
  label: string
  value: 'yes' | 'no' | ''
  disabled?: boolean
  /** id сообщения об ошибке — единственный способ объявить незаполненность: `aria-invalid`
   *  роль `group` не поддерживает (jsx-a11y/role-supports-aria-props). */
  describedBy?: string
  onChange: (next: 'yes' | 'no') => void
}) {
  return (
    <div className="flex gap-2" role="group" aria-label={label} aria-describedby={describedBy}>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className={cn('min-w-16', value === 'yes' && SELECTED_ANSWER)}
        aria-pressed={value === 'yes'}
        disabled={disabled}
        onClick={() => onChange('yes')}
      >
        {PROFRISK_YES}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className={cn('min-w-16', value === 'no' && SELECTED_ANSWER)}
        aria-pressed={value === 'no'}
        disabled={disabled}
        onClick={() => onChange('no')}
      >
        {PROFRISK_NO}
      </Button>
    </div>
  )
}

// --- Файлы списка инструкций: форматы и ошибки ---

/**
 * Допустимые форматы файлов списка инструкций (требование «Способ „Загрузить файлы“»)
 * — зеркало серверного `files.DocsImagesExt`: расширение проверяется и на клиенте, чтобы
 * заведомо неподходящий файл не уходил в сеть и ошибка появлялась мгновенно.
 */
const INSTRUCTION_FILE_EXT = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png']
const INSTRUCTION_FILE_ACCEPT = INSTRUCTION_FILE_EXT.join(',')
/** Дословный текст требования для недопустимого формата (совпадает с серверным). */
const INSTRUCTION_FILE_FORMAT_ERROR =
  'Недопустимый формат файла. Загрузите PDF, Word, Excel или изображение.'

function hasAllowedExt(name: string): boolean {
  const lower = name.toLowerCase()
  return INSTRUCTION_FILE_EXT.some((ext) => lower.endsWith(ext))
}

/** Сообщение об отказе загрузки: 422 по полю `file` отдаёт дословный текст требования. */
function uploadErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 422) {
    const detail = (err.details ?? []).find((d) => d.field === 'file')
    if (detail) return detail.message
  }
  return getErrorMessage(err, 'Не удалось загрузить файл')
}

/** Файл, который не удалось загрузить: живёт только в UI, показывается строкой с ошибкой. */
interface FailedUpload {
  key: string
  name: string
  message: string
}

let uploadKeySeq = 0
const nextUploadKey = () => `upl-${(uploadKeySeq += 1)}`

let instructionKeySeq = 0
const nextInstructionKey = () => `ins-${(instructionKeySeq += 1)}`

/**
 * Состояние загрузки файлов: сами запросы (загрузка/замена/удаление/очистка), флаг «идёт
 * работа», строки неудачных загрузок и общая ошибка блока. Вынесено из компонента, чтобы
 * разметка и сценарий не перемешивались с сетью.
 */
function useInstructionFiles({
  orderId,
  files,
  onFilesChange,
}: {
  orderId: string
  files: ProfriskInstructionFile[]
  onFilesChange: (next: ProfriskInstructionFile[]) => void
}) {
  const [failed, setFailed] = useState<FailedUpload[]>([])
  const [busy, setBusy] = useState(false)
  const [blockError, setBlockError] = useState<string | null>(null)

  function addFailed(name: string, message: string) {
    setFailed((prev) => [...prev, { key: nextUploadKey(), name, message }])
  }

  /**
   * Загрузка выбранных/перетащенных файлов: КАЖДЫЙ файл — независимый запрос (API принимает
   * ровно один файл, design D4). Отказ по одному файлу оседает строкой с ошибкой рядом с ним и
   * не отменяет остальные загрузки и не трогает введённые данные шага.
   */
  async function uploadFiles(list: File[]) {
    if (list.length === 0) return
    setBusy(true)
    const uploaded: ProfriskInstructionFile[] = []
    await Promise.all(
      list.map(async (file) => {
        if (!hasAllowedExt(file.name)) {
          addFailed(file.name, INSTRUCTION_FILE_FORMAT_ERROR)
          return
        }
        try {
          uploaded.push(await profriskApi.uploadInstructionFile(orderId, file))
        } catch (err) {
          addFailed(file.name, uploadErrorMessage(err))
        }
      }),
    )
    if (uploaded.length > 0) onFilesChange([...files, ...uploaded])
    setBusy(false)
  }

  /** Замена файла = загрузка нового + удаление старого (отдельного эндпоинта замены нет, D4). */
  async function replaceFile(target: ProfriskInstructionFile, file: File) {
    if (!hasAllowedExt(file.name)) {
      addFailed(file.name, INSTRUCTION_FILE_FORMAT_ERROR)
      return
    }
    setBusy(true)
    try {
      const next = await profriskApi.uploadInstructionFile(orderId, file)
      await profriskApi.deleteInstructionFile(orderId, target.id)
      onFilesChange(files.map((f) => (f.id === target.id ? next : f)))
    } catch (err) {
      addFailed(file.name, uploadErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function removeFile(target: ProfriskInstructionFile) {
    setBusy(true)
    setBlockError(null)
    try {
      await profriskApi.deleteInstructionFile(orderId, target.id)
      onFilesChange(files.filter((f) => f.id !== target.id))
    } catch (err) {
      setBlockError(getErrorMessage(err, 'Не удалось удалить файл'))
    } finally {
      setBusy(false)
    }
  }

  /** Удаление всех загруженных файлов (смена способа/ответ «Нет»): не удалённые остаются в списке. */
  async function clearFiles() {
    if (files.length === 0) return
    const results = await Promise.allSettled(
      files.map((f) => profriskApi.deleteInstructionFile(orderId, f.id)),
    )
    const remaining = files.filter((_, i) => results[i].status === 'rejected')
    onFilesChange(remaining)
    setBlockError(remaining.length > 0 ? 'Не удалось удалить часть файлов — попробуйте ещё раз' : null)
  }

  function dismissFailed(key: string) {
    setFailed((prev) => prev.filter((x) => x.key !== key))
  }

  function resetFailed() {
    setFailed([])
  }

  return {
    failed,
    busy,
    setBusy,
    blockError,
    uploadFiles,
    replaceFile,
    removeFile,
    clearFiles,
    dismissFailed,
    resetFailed,
  }
}

// --- Часть: выбор способа передачи списка ---

function MethodPicker({
  method,
  disabled,
  error,
  onSelect,
}: {
  method: ProfriskInstructionsMethod
  disabled: boolean
  error: string | null
  onSelect: (next: ProfriskInstructionsMethod) => void
}) {
  return (
    <>
      <p className="mb-2 text-14 text-txt">{PROFRISK_INSTRUCTIONS_METHOD_QUESTION}</p>
      <div className="grid gap-3 nw:grid-cols-2" role="group" aria-label={PROFRISK_INSTRUCTIONS_METHOD_QUESTION}>
        <ServicePickCard
          title={PROFRISK_METHOD_CARDS.files.title}
          description={PROFRISK_METHOD_CARDS.files.description}
          selected={method === 'files'}
          disabled={disabled}
          onSelect={() => onSelect('files')}
        />
        <ServicePickCard
          title={PROFRISK_METHOD_CARDS.manual.title}
          description={PROFRISK_METHOD_CARDS.manual.description}
          selected={method === 'manual'}
          disabled={disabled}
          onSelect={() => onSelect('manual')}
        />
      </div>
      {error ? (
        <p role="alert" className="mt-1.5 text-12 text-red">
          {error}
        </p>
      ) : null}
    </>
  )
}

// --- Часть: зона загрузки (перетаскивание + скрытые input'ы выбора и замены) ---

function UploadZone({
  busy,
  pickRef,
  replaceRef,
  onPickFiles,
  onPickReplacement,
}: {
  busy: boolean
  pickRef: RefObject<HTMLInputElement>
  /** Скрытый input замены живёт здесь, а нажимает его строка файла (см. `FilesMethod`). */
  replaceRef: RefObject<HTMLInputElement>
  onPickFiles: (list: File[]) => void
  onPickReplacement: (file: File | undefined) => void
}) {
  const [dragOver, setDragOver] = useState(false)

  return (
    // Зона перетаскивания — не интерактивный элемент сама по себе: клавиатурный путь
    // даёт кнопка «Выбрать файлы» внутри неё (drag&drop с клавиатуры невозможен).
    <div
      className={
        'rounded-12 border border-dashed p-5 text-center ' +
        (dragOver ? 'border-accent bg-accent-bg' : 'border-line-strong')
      }
      data-testid="profrisk-instructions-dropzone"
      onDragOver={(e) => {
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragOver(false)
        onPickFiles(Array.from(e.dataTransfer.files))
      }}
    >
      <p className="text-14 text-txt-secondary">{PROFRISK_UPLOAD_ZONE_TEXT}</p>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className="mt-3"
        disabled={busy}
        onClick={() => pickRef.current?.click()}
        leftIcon={<Upload className="size-4 stroke-current" aria-hidden />}
      >
        {PROFRISK_UPLOAD_PICK_BUTTON}
      </Button>
      <p className="mt-2 text-12 text-txt-tertiary">{PROFRISK_UPLOAD_ZONE_FORMATS}</p>
      <input
        ref={pickRef}
        type="file"
        multiple
        accept={INSTRUCTION_FILE_ACCEPT}
        className="hidden"
        aria-label={PROFRISK_UPLOAD_PICK_BUTTON}
        onChange={(e) => {
          const list = Array.from(e.target.files ?? [])
          e.target.value = ''
          onPickFiles(list)
        }}
      />
      <input
        ref={replaceRef}
        type="file"
        accept={INSTRUCTION_FILE_ACCEPT}
        className="hidden"
        aria-label={PROFRISK_UPLOAD_REPLACE_BUTTON}
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          onPickReplacement(file)
        }}
      />
    </div>
  )
}

// --- Часть: список загруженных файлов (и строки неудачных загрузок) ---

function FileList({
  files,
  failed,
  busy,
  readOnly,
  onReplaceRequest,
  onRemove,
  onDismissFailed,
}: {
  files: ProfriskInstructionFile[]
  failed: FailedUpload[]
  busy: boolean
  readOnly: boolean
  onReplaceRequest: (target: ProfriskInstructionFile) => void
  onRemove: (target: ProfriskInstructionFile) => void
  onDismissFailed: (key: string) => void
}) {
  return (
    <ul className="mt-3 flex flex-col gap-2">
      {files.map((f) => (
        <li
          key={f.id}
          className="flex flex-col gap-2 rounded-8 border border-line px-3 py-2 nw:flex-row nw:items-center nw:justify-between"
        >
          <a className="text-13 text-accent underline" href={f.url} download>
            {f.name}
          </a>
          {!readOnly ? (
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={busy}
                aria-label={`${PROFRISK_UPLOAD_REPLACE_BUTTON}: ${f.name}`}
                onClick={() => onReplaceRequest(f)}
              >
                {PROFRISK_UPLOAD_REPLACE_BUTTON}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={busy}
                aria-label={`${PROFRISK_UPLOAD_DELETE_BUTTON}: ${f.name}`}
                onClick={() => onRemove(f)}
                leftIcon={<Trash2 className="size-4 stroke-current" aria-hidden />}
              >
                {PROFRISK_UPLOAD_DELETE_BUTTON}
              </Button>
            </div>
          ) : null}
        </li>
      ))}
      {/* Неудачные загрузки — отдельными строками с сообщением РЯДОМ с файлом. */}
      {failed.map((f) => (
        <li key={f.key} className="rounded-8 border border-red px-3 py-2">
          <span className="text-13 text-txt">{f.name}</span>
          <p role="alert" className="mt-1 text-12 text-red">
            {f.message}
          </p>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="mt-2"
            aria-label={`Убрать сообщение: ${f.name}`}
            onClick={() => onDismissFailed(f.key)}
          >
            Скрыть
          </Button>
        </li>
      ))}
    </ul>
  )
}

// --- Часть: способ «Загрузить файлы» целиком ---

function FilesMethod({
  files,
  failed,
  busy,
  readOnly,
  error,
  onUpload,
  onReplace,
  onRemove,
  onDismissFailed,
}: {
  files: ProfriskInstructionFile[]
  failed: FailedUpload[]
  busy: boolean
  readOnly: boolean
  error: string | null
  onUpload: (list: File[]) => void
  onReplace: (target: ProfriskInstructionFile, file: File) => void
  onRemove: (target: ProfriskInstructionFile) => void
  onDismissFailed: (key: string) => void
}) {
  const pickRef = useRef<HTMLInputElement>(null)
  const replaceRef = useRef<HTMLInputElement>(null)
  const replaceTarget = useRef<ProfriskInstructionFile | null>(null)

  return (
    <div className="mt-4">
      {!readOnly ? (
        <UploadZone
          busy={busy}
          pickRef={pickRef}
          replaceRef={replaceRef}
          onPickFiles={onUpload}
          onPickReplacement={(file) => {
            const target = replaceTarget.current
            replaceTarget.current = null
            if (file && target) onReplace(target, file)
          }}
        />
      ) : null}

      {files.length === 0 && failed.length === 0 && readOnly ? (
        <p className="py-2 text-txt-secondary">Файлы не переданы.</p>
      ) : null}

      <FileList
        files={files}
        failed={failed}
        busy={busy}
        readOnly={readOnly}
        onReplaceRequest={(f) => {
          replaceTarget.current = f
          replaceRef.current?.click()
        }}
        onRemove={onRemove}
        onDismissFailed={onDismissFailed}
      />
      {error ? (
        <p role="alert" className="mt-1.5 text-12 text-red">
          {error}
        </p>
      ) : null}
    </div>
  )
}

// --- Часть: способ «Ввести вручную» ---

function ManualList({
  instructions,
  readOnly,
  error,
  onChangeRow,
  onAddRow,
  onRemoveRow,
}: {
  instructions: ProfriskInstruction[]
  readOnly: boolean
  error: string | null
  onChangeRow: (i: number, name: string) => void
  onAddRow: () => void
  onRemoveRow: (i: number) => void
}) {
  // Стабильные клиентские ключи строк ручного списка: индексный key ломал бы фокус/курсор
  // при удалении строки из середины.
  const keysRef = useRef<string[]>([])
  while (keysRef.current.length < instructions.length) keysRef.current.push(nextInstructionKey())
  if (keysRef.current.length > instructions.length) {
    keysRef.current = keysRef.current.slice(0, instructions.length)
  }

  function removeRow(i: number) {
    keysRef.current = keysRef.current.filter((_, j) => j !== i)
    onRemoveRow(i)
  }

  return (
    <div className="mt-4">
      {instructions.map((row, i) => (
        <div key={keysRef.current[i]} className="mb-3 flex flex-col gap-3 nw:flex-row nw:items-end">
          <Field label={PROFRISK_MANUAL_NAME_LABEL} className="my-0 nw:flex-1">
            {(props) => (
              <Input
                {...props}
                value={row.name}
                disabled={readOnly}
                onChange={(e) => onChangeRow(i, e.target.value)}
                placeholder={i === 0 ? PROFRISK_MANUAL_FIRST_PLACEHOLDER : undefined}
              />
            )}
          </Field>
          {!readOnly ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              aria-label={`Удалить инструкцию №${i + 1}`}
              onClick={() => removeRow(i)}
              leftIcon={<Trash2 className="size-4 stroke-current" aria-hidden />}
            >
              Удалить
            </Button>
          ) : null}
        </div>
      ))}
      {!readOnly ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onAddRow}
          leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
        >
          {PROFRISK_MANUAL_ADD_BUTTON}
        </Button>
      ) : null}
      {error ? (
        <p role="alert" className="mt-1.5 text-12 text-red">
          {error}
        </p>
      ) : null}
    </div>
  )
}

// --- Часть: диалоги подтверждения ---

/** Ожидающее подтверждения действие блока (диалоги «Изменить способ…»/«Указать, что…»). */
type PendingChange =
  | { kind: 'method'; value: ProfriskInstructionsMethod }
  | { kind: 'none' }

function ConfirmDialog({
  pending,
  onCancel,
  onConfirm,
}: {
  /** null — диалога нет; вид диалога определяется видом ожидающего действия. */
  pending: PendingChange | null
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <Dialog
      open={pending !== null}
      onOpenChange={(next) => {
        if (!next) onCancel()
      }}
    >
      {pending ? (
        <DialogContent
          title={pending.kind === 'method' ? PROFRISK_CONFIRM_METHOD_TITLE : PROFRISK_CONFIRM_NONE_TITLE}
          description={pending.kind === 'method' ? PROFRISK_CONFIRM_METHOD_TEXT : PROFRISK_CONFIRM_NONE_TEXT}
          className="max-w-sm"
        >
          <Card
            header={
              pending.kind === 'method' ? PROFRISK_CONFIRM_METHOD_TITLE : PROFRISK_CONFIRM_NONE_TITLE
            }
          >
            <p className="text-txt-secondary">
              {pending.kind === 'method' ? PROFRISK_CONFIRM_METHOD_TEXT : PROFRISK_CONFIRM_NONE_TEXT}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" variant="secondary" onClick={onCancel}>
                {PROFRISK_CONFIRM_CANCEL}
              </Button>
              <Button size="sm" onClick={onConfirm}>
                {PROFRISK_CONFIRM_CONTINUE}
              </Button>
            </div>
          </Card>
        </DialogContent>
      ) : null}
    </Dialog>
  )
}

// --- Оркестратор блока ---

export interface InstructionsBlockPatch {
  instructionsDeveloped?: ProfriskInstructionsDeveloped
  instructionsMethod?: ProfriskInstructionsMethod
  instructions?: ProfriskInstruction[]
}

export interface InstructionsBlockProps {
  orderId: string
  developed: ProfriskInstructionsDeveloped
  method: ProfriskInstructionsMethod
  instructions: ProfriskInstruction[]
  files: ProfriskInstructionFile[]
  readOnly: boolean
  /** Незаполненный элемент блока (показывается только по попытке перехода). */
  errorField: ProfriskInstructionsErrorField | null
  onChange: (patch: InstructionsBlockPatch) => void
  onFilesChange: (next: ProfriskInstructionFile[]) => void
}

export function InstructionsBlock({
  orderId,
  developed,
  method,
  instructions,
  files,
  readOnly,
  errorField,
  onChange,
  onFilesChange,
}: InstructionsBlockProps) {
  const [pending, setPending] = useState<PendingChange | null>(null)
  const {
    failed,
    busy,
    setBusy,
    blockError,
    uploadFiles,
    replaceFile,
    removeFile,
    clearFiles,
    dismissFailed,
    resetFailed,
  } = useInstructionFiles({ orderId, files, onFilesChange })

  const block = {
    instructionsDeveloped: developed,
    instructionsMethod: method,
    instructions,
    instructionFiles: files,
  }

  function requestDeveloped(next: 'yes' | 'no') {
    if (next === developed) return
    if (next === 'no' && instructionsHaveData(block)) {
      setPending({ kind: 'none' })
      return
    }
    if (next === 'no') {
      onChange({ instructionsDeveloped: 'no', instructionsMethod: '', instructions: [] })
      return
    }
    onChange({ instructionsDeveloped: 'yes' })
  }

  function requestMethod(next: ProfriskInstructionsMethod) {
    if (next === method) return
    if (methodHasData(block)) {
      setPending({ kind: 'method', value: next })
      return
    }
    // Требование: при первом выборе способа «Ввести вручную» показывается одна пустая строка.
    onChange({
      instructionsMethod: next,
      instructions: next === 'manual' ? [emptyInstruction()] : [],
    })
  }

  async function applyPending() {
    const change = pending
    setPending(null)
    if (!change) return
    resetFailed()
    setBusy(true)
    try {
      if (change.kind === 'method') {
        if (method === 'files') await clearFiles()
        onChange({
          instructionsMethod: change.value,
          instructions: change.value === 'manual' ? [emptyInstruction()] : [],
        })
        return
      }
      await clearFiles()
      onChange({ instructionsDeveloped: 'no', instructionsMethod: '', instructions: [] })
    } finally {
      setBusy(false)
    }
  }

  function updateRow(i: number, name: string) {
    onChange({ instructions: instructions.map((row, j) => (j === i ? { name } : row)) })
  }
  function addRow() {
    onChange({ instructions: [...instructions, emptyInstruction()] })
  }
  function removeRow(i: number) {
    onChange({ instructions: instructions.filter((_, j) => j !== i) })
  }

  const developedError =
    errorField === 'instructionsDeveloped' ? PROFRISK_INSTRUCTIONS_ERRORS.instructionsDeveloped : null
  const methodError =
    errorField === 'instructionsMethod' ? PROFRISK_INSTRUCTIONS_ERRORS.instructionsMethod : null
  const filesError =
    errorField === 'instructionFiles' ? PROFRISK_INSTRUCTIONS_ERRORS.instructionFiles : null
  const manualError =
    errorField === 'instructions' ? PROFRISK_INSTRUCTIONS_ERRORS.instructions : null

  return (
    <Card header={PROFRISK_INSTRUCTIONS_TITLE}>
      {blockError ? (
        <Alert variant="danger" className="mb-3">
          {blockError}
        </Alert>
      ) : null}

      <p className="mb-2 text-14 text-txt">{PROFRISK_INSTRUCTIONS_QUESTION}</p>
      <YesNoGroup
        label={PROFRISK_INSTRUCTIONS_QUESTION}
        value={developed}
        disabled={readOnly || busy}
        describedBy={developedError ? 'profrisk-instructions-developed-error' : undefined}
        onChange={requestDeveloped}
      />
      {developedError ? (
        <p id="profrisk-instructions-developed-error" role="alert" className="mt-1.5 text-12 text-red">
          {developedError}
        </p>
      ) : null}

      {/* Ответ «Нет» закрывает блок: остальные элементы не отображаются. */}
      {developed === 'yes' ? (
        <div className="mt-5">
          <MethodPicker
            method={method}
            disabled={readOnly || busy}
            error={methodError}
            onSelect={requestMethod}
          />

          {method === 'files' ? (
            <FilesMethod
              files={files}
              failed={failed}
              busy={busy}
              readOnly={readOnly}
              error={filesError}
              onUpload={(list) => void uploadFiles(list)}
              onReplace={(target, file) => void replaceFile(target, file)}
              onRemove={(target) => void removeFile(target)}
              onDismissFailed={dismissFailed}
            />
          ) : null}

          {method === 'manual' ? (
            <ManualList
              instructions={instructions}
              readOnly={readOnly}
              error={manualError}
              onChangeRow={updateRow}
              onAddRow={addRow}
              onRemoveRow={removeRow}
            />
          ) : null}
        </div>
      ) : null}

      {/* Диалоги подтверждения: показываются ТОЛЬКО когда данные выбранным способом уже введены. */}
      <ConfirmDialog
        pending={pending}
        onCancel={() => setPending(null)}
        onConfirm={() => void applyPending()}
      />
    </Card>
  )
}
